import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Loader2, PackageCheck, Wrench, Search, Clock, MapPin, Phone, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

const STATUT_STEPS = ["recu", "diagnostic", "attente_piece", "reparation", "pret", "termine"];

const STATUT_ICONS = {
  recu: PackageCheck,
  diagnostic: Search,
  attente_piece: Clock,
  reparation: Wrench,
  pret: CheckCircle2,
  termine: CheckCircle2,
};

export default function PublicSuiviPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/public/suivi/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Lien de suivi introuvable"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 px-4 text-center">
        <XCircle size={40} className="text-destructive mb-3" />
        <p className="font-heading font-semibold text-lg mb-1">Lien de suivi introuvable</p>
        <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
      </div>
    );
  }

  const stepIndex = STATUT_STEPS.indexOf(data.statut);
  const Icon = STATUT_ICONS[data.statut] || Wrench;

  return (
    <div className="min-h-screen bg-secondary/30 px-4 py-10 flex flex-col items-center">
      <div className="w-full max-w-md space-y-5">
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Suivi de réparation</p>
          <p className="font-heading text-xl font-bold" data-testid="suivi-numero">{data.numero}</p>
        </div>

        {data.annule && (
          <div className="flex items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3" data-testid="suivi-annule">
            <AlertCircle size={18} className="shrink-0" />
            <p className="text-sm font-medium">Cette intervention a été annulée. Contactez la boutique pour plus d'informations.</p>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="suivi-status-card">
          <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Icon size={26} />
          </div>
          <p className="font-heading text-lg font-bold" data-testid="suivi-statut-label">{data.statut_label}</p>
          {data.materiel && <p className="text-sm text-muted-foreground mt-1">{data.materiel}</p>}
          {data.date_depot && <p className="text-xs text-muted-foreground mt-1">Déposé le {data.date_depot}</p>}
        </div>

        {stepIndex >= 0 && !data.annule && (
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="suivi-progress">
            <div className="flex items-center justify-between">
              {STATUT_STEPS.map((s, i) => (
                <div key={s} className="flex-1 flex flex-col items-center">
                  <div
                    className={`w-3 h-3 rounded-full ${i <= stepIndex ? "bg-primary" : "bg-secondary border border-border"}`}
                    data-testid={`suivi-step-${s}`}
                  />
                  {i < STATUT_STEPS.length - 1 && (
                    <div className={`h-0.5 w-full mt-1.5 ${i < stepIndex ? "bg-primary" : "bg-border"}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(data.boutique?.nom || data.boutique?.telephone) && (
          <div className="rounded-2xl border border-border bg-card p-5 space-y-2" data-testid="suivi-boutique">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Votre boutique</p>
            {data.boutique.nom && <p className="text-sm font-semibold">{data.boutique.nom}</p>}
            {(data.boutique.adresse || data.boutique.ville) && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <MapPin size={13} className="shrink-0 mt-0.5" />
                {[data.boutique.adresse, [data.boutique.code_postal, data.boutique.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
              </p>
            )}
            {data.boutique.telephone && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Phone size={13} className="shrink-0" /> {data.boutique.telephone}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
